import { serverTimestamp, type Timestamp } from "firebase/firestore";
import { action, computed, makeObservable } from "mobx";
import { TKey } from "../../../shared/translations/translationKeys";
import { ActivityEventType } from "../../activities/types";
import type { Api } from "../../core/apis";
import { BaseModel, type BaseModelData } from "../../core/BaseModel";
import type { Stores } from "../../core/stores";
import { trackEvent } from "../../core/trackEvent";
import type { EditorValue } from "../../ui/YooptaEditor";
import { sanitizeForFirestore } from "./sanitizeForFirestore";

export enum ModuleDataType {
  Checkbox = "checkbox",
  CalendarItem = "calendarItem",
  PollOption = "poll_option",
  Rating = "rating",
  PollVote = "poll_vote",
  Location = "location",
  Expense = "expense",
  Settlement = "settlement",
  TrackerItem = "tracker_item",
  RsvpUser = "rsvp_user",
}

export interface BaseModuleModelData extends BaseModelData {
  planUid: string;
  moduleUid: string;
  type: ModuleDataType;
  index: number;
  timestamp: Timestamp;
  descriptionYooptaEditor: EditorValue | null;
}

export default abstract class BaseModuleDataModel<
  T extends BaseModuleModelData,
> extends BaseModel<T> {
  constructor(stores: Stores, api: Api, data: T) {
    super(stores, api, data);
    Object.assign(this.data, {
      planUid: data.planUid ?? this.plan.uid,
      index: data.index ?? -1,
      descriptionYooptaEditor: data.descriptionYooptaEditor ?? null,
    });

    makeObservable(this);
  }

  @computed get hasDescription() {
    return !!(
      this.data.descriptionYooptaEditor &&
      !!Object.keys(this.data.descriptionYooptaEditor).length
    );
  }

  sendTelemetryEvent(event: string) {
    void trackEvent(`${this.data.type}_${event}`);
  }

  updateTimestamp() {
    this.data.timestamp = serverTimestamp() as Timestamp;
  }

  @action updateIndex(index: number) {
    if (index !== this.data.index) {
      this.data.index = index;
      this.api.modulesDataApi.set(this);
    }
  }

  @action setActivitySheetOpen(open: boolean) {
    this.module.setActivitySheetOpen(open);
  }

  @action setDescriptionEditor = async (data: EditorValue | null) => {
    if (data !== this.data.descriptionYooptaEditor) {
      const sanitizedData = {
        data: sanitizeForFirestore(data?.data),
        markdown: data?.markdown,
      };
      this.data.descriptionYooptaEditor = sanitizedData;
      this.api.modulesDataApi.set(this);
    }
  };

  @computed get dataItems() {
    return this.stores.modulesDataStore.all.filter(
      (event) => event.data.moduleUid === this.uid,
    );
  }

  @action async deleteData(label?: string) {
    const module = this.module;

    if (!module.isEditable) {
      return false;
    }

    const dataUid = this.uid;
    await this.api.modulesDataApi.delete(this);

    module.updateSnapshotWithoutSave();
    await module.save();

    if (label) {
      module.postActivityEvent(
        ActivityEventType.DataDeleted,
        TKey.ActivityDataWithLabelDeleted,
        { label: label },
        dataUid,
      );
    }
  }

  @computed get module() {
    const module = this.stores.modulesStore.byId.get(this.data.moduleUid);
    if (!module) {
      throw Error("No module found!");
    }
    return module;
  }

  @computed get plan() {
    const module = this.module;
    const plan = this.stores.plansStore.byId.get(module.data.planUid);
    if (!plan) {
      throw Error("No plan found!");
    }
    return plan;
  }

  toFireStore(): Omit<BaseModuleModelData, "uid"> {
    return {
      ...this.fromBaseModel(),
      ...this.data,
    };
  }

  abstract render(): JSX.Element;
}
